import { useEffect, useRef } from "react";
import * as echarts from "echarts/core";
import type { LeadSlice, MarketingReport } from "../lib/api.ts";
import type { DeltaFn } from "../lib/compare.ts";
import { compact, money, num, pctValue } from "../lib/format.ts";
import { chartTheme } from "../lib/chart.ts";
import { Card, Kpi } from "./widgets.tsx";
import { useThemeTick } from "./chrome.tsx";
import { Th, useSortable } from "./sortable.tsx";
import { useDrillOpener } from "./dealList.tsx";
import { TicketSourcesSection } from "./ticketSources.tsx";
import type { DrillDown } from "./leadList.tsx";
import { dbLabel, plural, t, type L } from "../i18n/lang.ts";
import { CHANNEL, CONVERSION_KIND, SENTINEL, UI } from "../i18n/dict.ts";

/**
 * Экран рекламных затрат.
 *
 * Каждая величина здесь названа тем, чем является: цена начатого диалога
 * (точная), цена лида CRM (оценка сверху) и доля рекламы в выручке парка
 * (отношение, а не отдача).
 *
 * ROMI появился 08.09.2026 по формуле заказчика — (выручка − расход) / расход —
 * и показан ВИЛКОЙ, а не числом. Причина в замере: за 01.07-08.09 к платному
 * источнику привязано 2 выигранные сделки из 273. Посчитанный только по
 * привязанным, ROMI даёт −93 % и измеряет не отдачу рекламы, а полноту
 * атрибуции; посчитанный по всей выручке периода — +584 %, потому что в него
 * попадают банкеты, пришедшие без рекламы. Обе границы честны, одна цифра между
 * ними была бы выдумкой. Сузить вилку может только клиент: UTM в ссылках
 * объявлений Google и запись gclid в лид.
 */

/** Проза этого экрана. Один блок в начале файла — см. App.tsx. */
const TX = {
  /* --- плитки расхода */
  adSpend: { ru: "Расход на рекламу", en: "Ad spend" },
  footAttributedToPark: { ru: "отнесено к парку", en: "attributed to a park" },
  footBrandLevel: { ru: "на уровне бренда", en: "brand-level" },
  whatsappConversations: { ru: "Диалоги WhatsApp", en: "WhatsApp conversations" },
  footStartedByMeta: {
    ru: "начатых, по подсчёту самой Meta",
    en: "started, as counted by Meta itself",
  },
  costPerConversationKpi: { ru: "Цена диалога", en: "Cost per conversation" },
  footCostExact: {
    ru: "точно: оба числа приходят из одного отчёта Meta",
    en: "exact: both numbers come from the same Meta report",
  },
  leadConversions: { ru: "Лидоподобные конверсии", en: "Lead-shaped conversions" },
  convInquiry: { ru: "обращения", en: "inquiries" },
  convDirections: { ru: "маршруты", en: "directions" },
  convOther: { ru: "прочее", en: "other" },
  convSplitHint: {
    ru: "Обращения — звонки, заявки с форм и чаты WhatsApp, начатые кнопкой в объявлении. Маршруты — нажатия «Маршрут» в Google Maps: так Performance Max на локальные магазины считает свою конверсию. Прочее — переходы с сайта и покупки.",
    en: "Inquiries are calls, form submissions and WhatsApp chats started from the button in an ad. Directions are taps on “Directions” in Google Maps — that is what Performance Max for local stores counts as its conversion. Other is clicks out of the site and purchases.",
  },
  /* Счётные существительные: в русском три формы. */
  footSubscriptionsApart: {
    one: {
      ru: "подписка и вовлечение считаются отдельно",
      en: "subscriptions and engagements are counted apart",
    },
    few: {
      ru: "подписки и вовлечения считаются отдельно",
      en: "subscriptions and engagements are counted apart",
    },
    many: {
      ru: "подписок и вовлечений считаются отдельно",
      en: "subscriptions and engagements are counted apart",
    },
  },
  clicks: {
    one: { ru: "клик", en: "click" },
    few: { ru: "клика", en: "clicks" },
    many: { ru: "кликов", en: "clicks" },
  },

  /* --- ROMI вилкой: границы и оговорки про то, чему тут можно верить */
  romiHint: {
    ru: "(выручка − расход) / расход · показан вилкой, потому что атрибуция неполна",
    en: "(revenue − spend) / spend · shown as a range, because attribution is incomplete",
  },
  lowerBound: { ru: "Нижняя граница", en: "Lower bound" },
  footRomiLower: {
    ru: "только сделки, прослеженные до платного источника",
    en: "only deals traced to a paid source",
  },
  upperBound: { ru: "Верхняя граница", en: "Upper bound" },
  footRomiUpper: {
    ru: "каждое завершённый праздник периода",
    en: "every completed event in the period",
  },
  /* Предлог перед числом событий. В русском взято «за»: оно даёт обычные формы
     счёта — за 1 событие, за 2 события, за 5 событий. */
  fromEvents: { ru: "за", en: "from" },
  events: {
    one: { ru: "праздник", en: "event" },
    few: { ru: "праздника", en: "events" },
    many: { ru: "праздников", en: "events" },
  },
  attributionCoverage: { ru: "Охват атрибуции", en: "Attribution coverage" },
  footCoverageEvents: {
    one: {
      ru: "завершённого события удаётся связать с рекламой",
      en: "completed event can be traced to an ad",
    },
    few: {
      ru: "завершённых праздников удаётся связать с рекламой",
      en: "completed events can be traced to an ad",
    },
    many: {
      ru: "завершённых праздников удаётся связать с рекламой",
      en: "completed events can be traced to an ad",
    },
  },
  romiBanquets: { ru: "Праздники", en: "Parties" },
  romiTickets: { ru: "Онлайн-билеты", en: "Online tickets" },
  footRomiLowerTickets: {
    ru: "только оплаты с меткой рекламы Google или Meta",
    en: "only payments carrying a Google or Meta ad tag",
  },
  footRomiUpperTickets: {
    ru: "все онлайн-билеты, оплаченные в периоде",
    en: "every online ticket paid in the period",
  },
  bookings: {
    one: { ru: "бронь", en: "booking" },
    few: { ru: "брони", en: "bookings" },
    many: { ru: "броней", en: "bookings" },
  },
  footCoverageBookings: {
    one: { ru: "оплаченной брони пришла с меткой рекламы", en: "paid booking carries an ad tag" },
    few: { ru: "оплаченных броней пришли с меткой рекламы", en: "paid bookings carry an ad tag" },
    many: { ru: "оплаченных броней пришли с меткой рекламы", en: "paid bookings carry an ad tag" },
  },
  spendBanquet: { ru: "Расход на праздники", en: "Party ad spend" },
  spendTickets: { ru: "Расход на билеты", en: "Ticket ad spend" },
  footSpendBanquet: {
    ru: "кампании со словом birthday в названии · знаменатель обеих границ",
    en: "campaigns with “birthday” in the name · the denominator of both bounds",
  },
  footSpendTickets: {
    ru: "кампании со словом ticket в названии · знаменатель обеих границ",
    en: "campaigns with “ticket” in the name · the denominator of both bounds",
  },
  drillRomiUpperTickets: {
    ru: "верх вилки — все онлайн-билеты, оплаченные в периоде",
    en: "upper bound — every online ticket paid in the period",
  },
  romiOther1: { ru: "Не входят ни в один ROMI:", en: "Not part of either ROMI:" },
  romiOther2: {
    ru: "— кампании без «birthday» или «ticket» в названии: посты, ретаргетинг, кампании сообщений. Весь расход периода —",
    en: "— campaigns without “birthday” or “ticket” in the name: boosted posts, retargeting, messaging campaigns. All spend in the period is",
  },
  romiNote1: {
    ru: "Правда лежит внутри этой вилки, и вилка широка по одной причине: метка не доживает до CRM. Из",
    en: "The truth sits inside this range, and the range is wide for one reason: the tag does not survive the trip into the CRM. Of",
  },
  romiNote2: {
    ru: "завершённых праздников этого периода",
    en: "completed events in this period,",
  },
  romiNote3: {
    ru: "удаётся связать с рекламой. Сузить вилку — на стороне клиента, а не на нашей: UTM-параметры в конечных URL объявлений Google и запись",
    en: "can be traced back to an ad. Narrowing it is on the client side, not ours — UTM parameters on the final URLs of Google ads, and the site storing",
  },
  romiNote4: {
    ru: "в лид, который создаёт сайт. До тех пор нижняя граница измеряет, насколько неполна атрибуция, а не то, как работает реклама.",
    en: "on the lead it creates. Until then, the lower bound measures how much attribution is missing, not how the advertising performs.",
  },

  /* --- раздельно по платформам */
  byPlatform: { ru: "По платформам", en: "By platform" },
  byPlatformHint: {
    ru: "слева рекламный кабинет, справа CRM — два разных счётчика, никогда не сумма",
    en: "ad account on the left, CRM on the right — two different counters, never a sum",
  },
  colPlatform: { ru: "Платформа", en: "Platform" },
  colSpendAed: { ru: "Расход, AED", en: "Spend, AED" },
  colClicks: { ru: "Клики", en: "Clicks" },
  colOtherConversions: { ru: "Прочие конверсии", en: "Other conversions" },
  colLeadsInCrm: { ru: "Обращения в CRM", en: "Inquiries in CRM" },
  colQualified: { ru: "Квалифицированные лиды", en: "Qualified leads" },
  colCompletedEvents: { ru: "Завершённые праздники", en: "Completed events" },
  colCostPerCrmLead: { ru: "Цена лида CRM", en: "Cost / CRM lead" },
  hintTaggedByAnalytics: {
    one: {
      ru: "лид CRM размечен аналитикой, а не измерен",
      en: "of the CRM leads were tagged by analytics, not measured",
    },
    few: {
      ru: "лида CRM размечены аналитикой, а не измерены",
      en: "of the CRM leads were tagged by analytics, not measured",
    },
    many: {
      ru: "лидов CRM размечено аналитикой, а не измерено",
      en: "of the CRM leads were tagged by analytics, not measured",
    },
  },
  drillLeadsInCrm: { ru: "лиды в CRM", en: "leads in the CRM" },
  drillHintPlatform: {
    ru: "лиды, чей источник сводится к этой платформе",
    en: "leads whose source resolves to this platform",
  },
  platformNote1: {
    /* `\u00A0` — неразрывный пробел: разряды числа и число со своим словом не
       должны разъезжаться по строкам. Записан escape-последовательностью, а не
       самим знаком: невидимый пробел в коде не отличить от обычного. */
    ru: "считают только те цели кампаний, у которых конверсия — это обращение или начатый диалог. Подписки и просмотры видео намеренно считаются отдельно. За всю историю их 30\u00A0103, из них 4\u00A0124 лидоподобные, а 24\u00A0080 — подписки на YouTube из кампаний Demand Gen — завышение в 7,3\u00A0раза, и именно поэтому колонки теперь раздельные.",
    en: "counts only campaign objectives whose conversion is an inquiry or a started conversation. Subscriptions and video views are counted apart on purpose. Across the whole history they sum to 30,103, of which 4,124 are lead-shaped and 24,080 are YouTube subscriptions from Demand Gen campaigns — a 7.3-fold overstatement, which is why the columns are now separate.",
  },
  platformNote2: {
    ru: "— другой счёт из другой системы: кабинет знает свои конверсии, CRM знает свои лиды, и в сумму они не складываются никогда. Там, где в строке Google конверсий намного больше, чем лидов CRM, этот разрыв — недостающая атрибуция: до лида не доходит ни один",
    en: "is a different count from a different system: the ad account knows its own conversions, the CRM knows its leads, and the two are never a sum. Where the Google row shows far more conversions than CRM leads, that gap is missing attribution — no",
  },
  platformNote3: {
    ru: "— а не расхождение между числами.",
    en: "reaches the lead — not a discrepancy between the numbers.",
  },

  /* --- источники со стороны CRM */
  bySource: { ru: "По источникам", en: "By source" },
  bySourceHint: {
    ru: "то же определение, что у «Источников» на «Обзоре»",
    en: "the same definition as Sources on Overview",
  },
  colBecameDeal: { ru: "Стали сделкой", en: "Became a deal" },
  colRevenueAed: { ru: "Выручка, AED", en: "Revenue, AED" },
  hintTaggedNotCrm: {
    ru: "размечено аналитикой, а не CRM",
    en: "tagged by analytics, not by the CRM",
  },
  drillAllLeads: { ru: "все лиды", en: "all leads" },
  drillCompletedEvents: { ru: "завершённые праздники", en: "completed events" },

  /* --- расход, не разнесённый по паркам */
  unknownSpendNote: {
    ru: "расхода не привязаны к парку — ни имя кампании, ни её цель парк не называют, поэтому эти деньги считаются в итоге, но ни в одной строке парка. Они показаны, а не спрятаны: спрятать их значило бы занизить расход и польстить каждому отношению на этом экране.",
    en: "of spend has no park in the campaign name and no park-specific objective, so it counts in the total but in no park row. It is shown rather than hidden: hiding it would understate cost and flatter every ratio on this screen.",
  },

  /* --- по паркам */
  byPark: { ru: "По паркам", en: "By park" },
  byParkHint: {
    ru: "три независимых источника, связанные только парком и периодом",
    en: "three independent sources joined only by park and period",
  },
  colAdSpendAed: { ru: "Расход на рекламу, AED", en: "Ad spend, AED" },
  colConversations: { ru: "Диалоги", en: "Conversations" },
  colCostPerConversation: { ru: "Цена диалога", en: "Cost / conv." },
  colLeadsCrm: { ru: "Обращения (CRM)", en: "Inquiries (CRM)" },
  colCostPerLead: { ru: "Цена лида", en: "Cost / lead" },
  colPosRevenueAed: { ru: "Касса, AED", en: "POS revenue, AED" },
  colAdsPerRevenue: { ru: "Реклама / выручка", en: "Ads / revenue" },
  noAdAccount: { ru: "нет рекламного кабинета", en: "no ad account" },
  /* Два разных случая, и путать их нельзя: «кабинета нет» — утверждение о
     кабинете, «за период нет расхода» — о периоде. До 16.09.2026 экран
     печатал первое всегда, и заказчик справедливо возразил: у Hello Park и
     Zamania кабинеты есть, просто расход в них шёл не весь период. */
  noSpendInPeriod: { ru: "за период нет расхода", en: "no spend in this period" },
  noSyrveAccess: { ru: "нет доступа к Syrve", en: "no Syrve access" },
  costPerLeadInline: { ru: "Цена лида", en: "Cost per lead" },
  parkNoteExact: {
    ru: "— точно: оба числа приходят из одного отчёта Meta.",
    en: "is exact — both numbers come from the same Meta report.",
  },
  parkNoteUpperBound: {
    ru: "— оценка сверху, а не атрибуция: в знаменателе стоят и органические, и телефонные лиды, поэтому лид, пришедший с рекламы, стоит на самом деле дороже.",
    en: "is an upper bound, not an attribution: organic and phone leads sit in the denominator too, so an ad-driven lead really costs more.",
  },
  parkNoteRatio: {
    ru: "— отношение, а не отдача: в кассу входит каждый гость, пришедший сам и рекламы не видевший. ROMI показан вилкой в начале этого экрана и никогда одним числом.",
    en: "is a ratio, not a return — till revenue includes every walk-in guest who never saw an ad. ROMI is shown as a range at the top of this screen, never as a single number.",
  },
  parkNoteLegacy: {
    ru: "Со включёнными старыми данными в счёт лидов попадает эпоха до июля, когда воронку не вели, поэтому цена лида здесь несравнима с рабочей эпохой.",
    en: "With legacy data included, lead counts contain the era before July when the funnel was not maintained, so cost per lead here is not comparable to the managed era.",
  },

  /* --- график по месяцам */
  spendAndLeadsByMonth: {
    ru: "Расход и обращения по месяцам",
    en: "Spend and conversations by month",
  },
  twoAxes: { ru: "две оси: величины разного порядка", en: "two axes: the magnitudes differ" },
  axisCount: { ru: "кол-во", en: "count" },

  /* --- как расход нашёл свой парк */

  /* --- кампании без парка */
  campaignsWithNoPark: { ru: "Кампании без парка", en: "Campaigns with no park" },
  campaignsWithNoParkHint: {
    ru: "рабочий список: добавить правило или спросить клиента",
    en: "work list: add a rule, or ask the client",
  },
  colObjective: { ru: "Цель", en: "Objective" },
  colPeriod: { ru: "Период", en: "Period" },

  /* --- топ кампаний */
  topCampaigns: { ru: "Топ кампаний", en: "Top campaigns" },
  topCampaignsHint: { ru: "по расходу за выбранный период", en: "by spend in the selected period" },
  colLeadForms: { ru: "Лид-формы", en: "Lead forms" },

  /* --- воронка по кампаниям */
  campaignLeadDeal: { ru: "Кампания → лид → сделка", en: "Campaign → lead → deal" },
  campaignLeadDealHint: {
    ru: "единственный разрез, где атрибуция доходит до кампании, а не только до парка",
    en: "the only slice where attribution reaches a campaign, not just a park",
  },
  colMeasured: { ru: "Измерено", en: "Measured" },
  colInferred: { ru: "Восстановлено", en: "Inferred" },
  colCompleted: { ru: "Завершено", en: "Completed" },
  funnelIgnoresFilter: {
    ru: "Эта таблица не подчиняется фильтру даты выше",
    en: "This table ignores the date filter above",
  },
  funnelNote1: {
    ru: "— атрибуция до кампании доходит всего до",
    en: "— campaign-level attribution reaches",
  },
  funnelNote2: {
    ru: "лидов за три года, и двухмесячное окно часто оставляло бы её пустой, а это прочиталось бы как «связи не существует».",
    en: "leads across three years, and a two-month window would often leave it empty, which would read as “no link exists”.",
  },
  funnelNote3: {
    ru: "— значит, что клик принёс идентификатор в ссылке.",
    en: "means the click carried an identifier in the link.",
  },
  funnelNote4: {
    ru: "— значит, что источник восстановлен по ссылке внутри первого сообщения WhatsApp — та же реклама, но установленная нами, а не сообщённая кликом.",
    en: "means the source was reconstructed from the link inside the first WhatsApp message — the same ad, but established by us rather than reported by the click.",
  },
  funnelNote5: {
    ru: "— объединение обоих, и именно по ним считается воронка. И это всё ещё",
    en: "is the union of both, and it is what the funnel is counted on. These are still",
  },
  notWord: { ru: "не", en: "not" },
  funnelNote6: {
    ru: "все лиды, которые дала кампания: делить на них расход — не цена лида. Большинство обращений не несёт ссылки вовсе, а Meta сообщает число диалогов WhatsApp, но не идентификатор диалога.",
    en: "all the leads a campaign produced: dividing spend by them is not a cost per lead. Most inquiries carry no link at all, and Meta reports conversation counts for WhatsApp but no conversation identifier.",
  },
  utmLevelsNote: {
    ru: "Идентификатор лежит не там, где его ждёшь:",
    en: "The identifier is not where you would expect it:",
  },
  utmHolds: { ru: "содержит", en: "holds a" },
  leadsCounted: {
    one: { ru: "лид", en: "lead" },
    few: { ru: "лида", en: "leads" },
    many: { ru: "лидов", en: "leads" },
  },
  utmLevelsTail: {
    ru: ". Разные шаблоны ссылок используют разные макросы — это и должна починить нормальная настройка UTM.",
    en: ". Different link templates use different macros — that is what the proper UTM setup has to fix.",
  },

  /* --- покрытие кабинетов */
  adAccountCoverage: { ru: "Покрытие рекламных кабинетов", en: "Ad account coverage" },
  adAccountCoverageHint: {
    ru: "что подключено, а что нет",
    en: "what is connected and what is not",
  },
  colAccount: { ru: "Кабинет", en: "Account" },
  colCurrency: { ru: "Валюта", en: "Currency" },
  colDataFrom: { ru: "Данные с", en: "Data from" },
  colDataTo: { ru: "Данные по", en: "Data to" },
  colState: { ru: "Состояние", en: "State" },
  stateDormant: { ru: "спит", en: "dormant" },
  stateActive: { ru: "активен", en: "active" },
  stateDisabled: { ru: "отключён", en: "disabled" },
  noAdAccountNote1: {
    ru: "Рекламный кабинет не подключён для",
    en: "No ad account is connected for",
  },
  noAdAccountNote2: {
    ru: "— кабинета, привязанного к этому парку, у нас нет: либо он живёт в отдельном бизнес-портфеле и его надо расшарить нашему приложению, либо он не попал в список синхронизируемых кабинетов. В таких парках стоит «нет рекламного кабинета», а не ноль.",
    en: "— we have no ad account tied to that park: either it lives in a separate business portfolio and must be shared with our app, or it is missing from the list of accounts we sync. Such parks read “no ad account”, not zero.",
  },
  /* --- детализация: заголовки списков и оговорки к ним */
  drillQualified: { ru: "квалифицированные лиды", en: "qualified leads" },
  drillLeadsWithDeal: { ru: "лиды, ставшие сделкой", en: "leads that became a deal" },
  drillLeadsWon: { ru: "лиды с завершённым праздником", en: "leads with a completed event" },
  drillParkLeads: { ru: "лиды CRM этого парка", en: "CRM leads of this park" },
  /* Панель лидов даты отбора сама не подписывает — в отличие от панели сделок, —
     а на этом экране все списки лидов стоят на дате создания лида. */
  drillHintCreated: { ru: "по дате создания лида", en: "by the date the lead was created" },
  drillHintDenominator: {
    ru: "список — только ЗНАМЕНАТЕЛЬ: лиды CRM. Числитель — расход рекламного кабинета, и лида за ним нет",
    en: "the list is the DENOMINATOR only: CRM leads. The numerator is ad-account spend, which has no lead behind it",
  },
  drillRomiLower: {
    ru: "низ вилки — сделки, прослеженные до платного источника",
    en: "lower bound — deals traced to a paid source",
  },
  drillRomiUpper: {
    ru: "верх вилки — все завершённые праздники периода",
    en: "upper bound — every completed event in the period",
  },
  drillHintRomiNumerator: {
    ru: "список — только выручка вилки; расход в знаменателе приходит из рекламного кабинета, и лида за ним нет",
    en: "the list is the revenue side only; the spend in the denominator comes from the ad account and has no lead behind it",
  },
  drillCoverageNumerator: { ru: "числитель охвата", en: "the numerator of the coverage" },
  drillHintCoverageNumerator: {
    ru: "список — только ЧИСЛИТЕЛЬ: прослеженные сделки. Знаменатель — все завершённые праздники периода",
    en: "the list is the NUMERATOR only: traced deals. The denominator is every completed event in the period",
  },
  drillCampaignDeals: {
    ru: "сделки, чей лид несёт метку этой кампании",
    en: "deals whose lead carries this campaign's tag",
  },
  drillHintCampaignDeals: {
    ru: "доходит только размеченная часть — это НЕ все сделки кампании",
    en: "only the tagged part is reachable — these are NOT all the deals of the campaign",
  },
  topCampaignsDrillNote: {
    ru: "Имя кампании открывает сделки, чей лид несёт метку именно этой кампании, — по дате создания сделки. Метку доносит меньшинство обращений, поэтому у большинства строк список пуст: это значит «метка не дошла», а не «кампания не дала сделок». Расход, клики, диалоги и лид-формы в этой таблице — счётчики рекламного кабинета, и лида под ними нет ни одного.",
    en: "The campaign name opens the deals whose lead carries this campaign’s tag, by deal creation date. Only a minority of inquiries carry a tag, so most rows open an empty list: that means “the tag did not survive”, not “the campaign produced no deals”. Spend, clicks, conversations and lead forms in this table are ad-account counters with no lead behind them at all.",
  },
  dormantKept: {
    one: {
      ru: "синхронизируется ради истории, но больше не тратит.",
      en: "is kept in sync for history but no longer spending.",
    },
    few: {
      ru: "синхронизируются ради истории, но больше не тратят.",
      en: "are kept in sync for history but no longer spending.",
    },
    many: {
      ru: "синхронизируются ради истории, но больше не тратят.",
      en: "are kept in sync for history but no longer spending.",
    },
  },
} satisfies Record<string, L | { one: L; few: L; many: L }>;

/**
 * Тот же `t` из i18n под другим именем. Внутри графиков имя `t` занято объектом
 * темы (`const t = chartTheme()`), и вызвать там перевод было бы нечем.
 */
const chartLabel = t;

const aed = (m: string | number | null | undefined): string =>
  money(m, "AED", { withCode: false });

const aedShort = (m: string | number | null | undefined): string =>
  money(m, "AED", { compact: true, withCode: false });

export function MarketingScreen({
  report,
  era,
  onDrill,
  delta,
}: {
  report: MarketingReport;
  era: "managed" | "all";
  /** Раскрытие цифры в список лидов. Без него таблицы остаются обычными. */
  onDrill?: (d: DrillDown) => void;
  /**
   * Сравнение с предыдущим периодом. Без него экран прежний: плитка, у которой
   * сравнивать нечем, просто не показывает значка.
   */
  delta?: DeltaFn;
}): React.ReactElement {
  const k = report.kpi;
  const romi = report.romi;
  const spend = Number(k.spend_aed_minor);
  const conversations = Number(k.conversations);
  const parkSpend = Number(k.spend_park_aed_minor);
  const unknown = Number(k.spend_unknown_aed_minor);
  const perConversation = conversations > 0 ? spend / conversations / 100 : null;
  const leadConversions = Number(k.lead_conversions);
  const convInquiry = Number(k.lead_inquiry_conversions);
  const convDirections = Number(k.lead_directions_conversions);
  /* Остаток, а не третья сумма: части обязаны сходиться с числом плитки. */
  const convOther = Math.max(0, leadConversions - convInquiry - convDirections);

  /* В примечании под таблицей — только парки БЕЗ кабинета. Парк, где кабинет
     есть, а расхода в периоде нет, в этот список попадать не должен: он не
     ждёт подключения, у него просто не было трат. */
  const noAds = report.byPark.filter((p) => !p.has_ad_data && !p.has_ad_account);
  const dormant = report.coverage.filter((c) => c.dormant);

  /**
   * Открывашка списка СДЕЛОК. Из контекста, а не пропсом: `onDrill` этого экрана
   * умеет только лиды, а вилка ROMI считается по сделкам и по дате их закрытия.
   * Хук зовётся безусловно и до всех остальных — порядок вызова хуков не должен
   * зависеть ни от данных, ни от того, подключена ли панель.
   */
  const openDeals = useDrillOpener();

  /** Обёртка-ссылка вокруг уже напечатанного значения. */
  const linkTo = (shown: string, open: () => void): React.ReactElement => (
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();
        open();
      }}
    >
      {shown}
    </a>
  );

  /**
   * Ячейка-ссылка на список ЛИДОВ. Тем же приёмом, что в `ChannelTable`: без
   * открывашки остаётся обычным числом, и на нуле тоже — пустой список в
   * таблице читается как «никого не было», а под нулём это утверждение никому
   * не нужно.
   */
  const leadCell = (
    value: number,
    title: string,
    hint: string,
    slice: LeadSlice,
  ): React.ReactNode => {
    if (onDrill === undefined || value === 0) return num(value);
    return linkTo(num(value), () => onDrill({ title, hint, slice }));
  };

  /**
   * Ячейка ОТНОШЕНИЯ. Показанное значение — цена лида, а список под ним —
   * только знаменатель, и подпись панели это говорит прямо: числитель приходит
   * из рекламного кабинета, где лида нет по природе данных. Единого списка «из
   * чего сложилась цена лида» не существует, и подсунуть вместо него список
   * лидов молча значило бы соврать про то, что человек видит.
   */
  const ratioCell = (
    shown: string,
    title: string,
    slice: LeadSlice,
  ): React.ReactNode =>
    onDrill === undefined
      ? shown
      : linkTo(shown, () =>
          onDrill({ title, hint: t(TX.drillHintDenominator), slice }),
        );

  /*
   * Сортировка таблиц экрана. Все восемь хуков объявлены здесь и без условий:
   * две таблицы ниже стоят под `length > 0`, а порядок вызова хуков менять
   * нельзя. Ни один вызов не меняет порядок по умолчанию — до первого клика
   * хук отдаёт массив как пришёл, то есть серверный `ORDER BY`.
   */

  const sPlatform = useSortable(report.byPlatform, {
    platform: {
      kind: "text",
      get: (pl) => (pl.platform === "google" ? "Google Ads" : "Meta Ads"),
    },
    spend: { kind: "number", get: (pl) => pl.spend_aed_minor },
    clicks: { kind: "number", get: (pl) => pl.clicks },
    leadConv: { kind: "number", get: (pl) => pl.lead_conversions },
    /* Ячейка складывает два поля и на нуле печатает прочерк — отсюда та же
       сумма и `null`, а не ноль: иначе платформа без прочих конверсий встала бы
       между настоящими значениями как измеренный ноль. */
    other: {
      kind: "number",
      get: (pl) => {
        const o = Number(pl.engagement_conversions) + Number(pl.awareness_conversions);
        return o === 0 ? null : o;
      },
    },
    crmLeads: { kind: "number", get: (pl) => pl.crm_leads },
    qualified: { kind: "number", get: (pl) => pl.crm_qualified },
    won: { kind: "number", get: (pl) => pl.crm_won },
    /* Цена лида CRM в ответе не существует, считается на месте рендера —
       повторяем ТУ ЖЕ формулу вместе с обоими условиями знаменателя. */
    perLead: {
      kind: "number",
      get: (pl) => {
        const s = Number(pl.spend_aed_minor);
        return pl.crm_leads > 0 && s > 0 ? s / pl.crm_leads / 100 : null;
      },
    },
  });

  const sSource = useSortable(report.bySource, {
    /* Подпись переводится через словарь каналов: сортировать по `source_group`
       значило бы показать человеку слова, а упорядочить ключи базы. */
    source: { kind: "text", get: (r) => dbLabel(CHANNEL, r.source_group, r.source_name) },
    leads: { kind: "number", get: (r) => r.leads },
    qualified: { kind: "number", get: (r) => r.qualified },
    withDeal: { kind: "number", get: (r) => r.with_deal },
    won: { kind: "number", get: (r) => r.won },
    revenue: { kind: "number", get: (r) => r.revenue_aed_minor },
  });

  const sPark = useSortable(report.byPark, {
    park: { kind: "text", get: (p) => dbLabel(SENTINEL, p.park_name, p.park_name) },
    /* Парк без кабинета печатает «нет рекламного кабинета», без кассы — «нет
       доступа к Syrve»: обе фразы обязаны дать `null` и уйти в конец, иначе
       отсутствие доступа прочитается как нулевой расход и нулевая выручка. */
    spend: { kind: "number", get: (p) => (p.has_ad_data ? p.spend_aed_minor : null) },
    conversations: { kind: "number", get: (p) => (p.has_ad_data ? p.conversations : null) },
    /* Три следующие колонки считаются в разметке и в ответе сервера их нет.
       Условие знаменателя повторено по ячейке буквально, включая то, что цена
       диалога проверяет только `conv > 0`. */
    perConv: {
      kind: "number",
      get: (p) => {
        const conv = Number(p.conversations);
        return conv > 0 ? Number(p.spend_aed_minor) / conv / 100 : null;
      },
    },
    leads: { kind: "number", get: (p) => p.leads },
    perLead: {
      kind: "number",
      get: (p) =>
        p.has_ad_data && p.leads > 0 ? Number(p.spend_aed_minor) / p.leads / 100 : null,
    },
    revenue: { kind: "number", get: (p) => (p.has_pos_data ? p.revenue_aed_minor : null) },
    adsPerRevenue: {
      kind: "number",
      get: (p) => {
        const s = Number(p.spend_aed_minor);
        const rev = Number(p.revenue_aed_minor);
        return p.has_pos_data && rev > 0 && s > 0 ? (100 * s) / rev : null;
      },
    },
  });

  /*
   * Кампании без парка приезжают запросом с `LIMIT 20` и БЕЗ `ORDER BY`
   * (`server/src/api/marketing.ts:224`). Пока строк меньше двадцати, видна вся
   * выборка и сортировать её честно; на двадцати таблица может быть срезом, и
   * тогда «самая дорогая» строка была бы максимумом по случайным двадцати —
   * заголовки выключаются сами и объясняют, почему.
   */
  const sUnattributed = useSortable(
    report.unattributed,
    {
      name: { kind: "text", get: (u) => u.name },
      objective: {
        kind: "text",
        get: (u) => (u.objective === null ? null : u.objective.replace("OUTCOME_", "")),
      },
      /* Ячейка печатает обе даты подряд; `numeric: true` у коллатора даёт по
         такой строке хронологию — сначала первый день, при равенстве последний. */
      period: { kind: "text", get: (u) => `${u.first_day} → ${u.last_day}` },
      spend: { kind: "number", get: (u) => u.spend_aed_minor },
      conversations: { kind: "number", get: (u) => u.messaging_conversations },
    },
    { partial: report.unattributed.length >= 20 },
  );

  /*
   * Топ кампаний — та самая таблица, где клиентская сортировка соврала бы: в
   * базе 267 кампаний, запрос отдаёт 100 (`marketing.ts:163—164`), разметка
   * показывает 40. Серверной сортировки у эндпоинта нет, поэтому таблица
   * помечена `partial` — заголовки некликабельны и говорят причину.
   */
  const sCampaign = useSortable(
    report.byCampaign.slice(0, 40),
    {
      campaign: { kind: "text", get: (c) => c.campaign_name },
      park: {
        kind: "text",
        get: (c) => (c.park_name === null ? null : dbLabel(SENTINEL, c.park_name, c.park_name)),
      },
      spend: { kind: "number", get: (c) => c.spend_aed_minor },
      clicks: { kind: "number", get: (c) => c.clicks },
      conversations: { kind: "number", get: (c) => c.conversations },
      perConv: {
        kind: "number",
        get: (c) => {
          const conv = Number(c.conversations);
          return conv > 0 ? Number(c.spend_aed_minor) / conv / 100 : null;
        },
      },
      leadForms: { kind: "number", get: (c) => c.platform_conversions },
    },
    { partial: true },
  );

  /* Воронка по кампаниям — `LIMIT 40` (`marketing.ts:279`). Условие то же, что
     у кампаний без парка: срез сортировать нельзя, полную выборку можно. */
  const sFunnel = useSortable(
    report.campaignFunnel,
    {
      campaign: { kind: "text", get: (c) => c.campaign_name },
      park: { kind: "text", get: (c) => c.park_key },
      spend: { kind: "number", get: (c) => c.spend_aed_minor },
      measured: { kind: "number", get: (c) => c.leads_with_utm },
      inferred: { kind: "number", get: (c) => c.leads_with_inferred_tag },
      leads: { kind: "number", get: (c) => c.leads_total },
      withDeal: { kind: "number", get: (c) => c.with_deal },
      won: { kind: "number", get: (c) => c.won_deal },
      revenue: { kind: "number", get: (c) => c.revenue_aed_minor },
    },
    { partial: report.campaignFunnel.length >= 40 },
  );

  const sCoverage = useSortable(report.coverage, {
    account: { kind: "text", get: (c) => c.name },
    platform: { kind: "text", get: (c) => c.platform },
    currency: { kind: "text", get: (c) => c.currency },
    brand: { kind: "text", get: (c) => c.brand_key },
    /* Границы данных приходят строкой `::text`, и ISO-дата сортируется текстом
       верно: сегменты выровнены по позициям. Кабинет без синхронизаций даёт
       `null` — прочерк в ячейке, конец списка в порядке. */
    dataFrom: { kind: "text", get: (c) => c.first_day },
    dataTo: { kind: "text", get: (c) => c.last_day },
    /* Состояние — три взаимоисключающих слова, собранных в разметке: сортируем
       по напечатанному слову, а не по паре флагов `dormant`/`enabled`. */
    state: {
      kind: "text",
      get: (c) =>
        c.dormant ? t(TX.stateDormant) : c.enabled ? t(TX.stateActive) : t(TX.stateDisabled),
    },
  });

  return (
    <>
      <div className="grid cols-4" style={{ marginBottom: 14 }}>
        {/*
          НИ ОДНА из четырёх плиток этой строки не раскрывается списком, и это
          не пропуск. Расход, диалоги WhatsApp и лидоподобные конверсии приходят
          из рекламного кабинета агрегатом «кампания за день»: идентификатора
          диалога Meta не отдаёт вовсе, а конверсию не сопоставляет ни с лидом,
          ни с человеком. Цена диалога — отношение, и обе его части лежат в том
          же агрегате, так что раскрыть нечего даже наполовину. Максимум
          честного здесь — список КАМПАНИЙ И ДНЕЙ; почему он не сделан, сказано
          в отчёте волны.

          Расход БЕЗ `inverse`: сколько тратить — это решение, а не результат, и
          красная стрелка объявила бы ошибкой любое увеличение бюджета.
        */}
        <Kpi
          label={t(TX.adSpend)}
          value={aedShort(spend)}
          unit="AED"
          foot={`${aedShort(parkSpend)} ${t(TX.footAttributedToPark)} · ${aedShort(Number(k.spend_brand_aed_minor))} ${t(TX.footBrandLevel)}`}
          delta={delta?.("kpi.spend_aed_minor")}
        />
        <Kpi
          label={t(TX.whatsappConversations)}
          value={num(conversations)}
          foot={t(TX.footStartedByMeta)}
          delta={delta?.("kpi.conversations")}
        />
        {/*
          Единственная плитка экрана, которой `inverse` был бы нужен: рост цены
          при том же результате — плохая новость. Сравнения у неё всё равно нет,
          потому что цена диалога считается здесь, на клиенте, а в ответе такого
          поля не существует — путь пришлось бы выдумать.
        */}
        <Kpi
          label={t(TX.costPerConversationKpi)}
          value={perConversation === null ? "—" : perConversation.toFixed(0)}
          unit="AED"
          foot={t(TX.footCostExact)}
        />
        {/*
          Разбивка по виду действия — первой строкой подписи (просьба
          31.10.2026: «разделить маршруты и обращения»). Число плитки то же:
          что кампания назвала конверсией. Но у Pmax на локальные магазины это
          нажатие «Маршрут» в Google Maps — 782 из 865 за октябрь 2026, — и без
          разбивки плитка читалась как 865 обращений. Прочее — остаток: переходы
          с сайта, покупки и копейки округления дробных конверсий Google.
        */}
        <Kpi
          label={t(TX.leadConversions)}
          value={num(leadConversions)}
          foot={
            <>
              <span style={{ display: "block" }} title={t(TX.convSplitHint)}>
                {t(TX.convInquiry)} {num(convInquiry)} · {t(TX.convDirections)} {num(convDirections)} ·{" "}
                {t(TX.convOther)} {num(convOther)}
              </span>
              <span style={{ display: "block" }}>
                {`${num(Number(k.engagement_conversions))} ${plural(Number(k.engagement_conversions), TX.footSubscriptionsApart)} · ${num(Number(k.clicks))} ${plural(Number(k.clicks), TX.clicks)}`}
              </span>
            </>
          }
          delta={delta?.("kpi.lead_conversions")}
        />
      </div>


      {/*
        ROMI вилкой.
        Обе границы стоят рядом с охватом атрибуции, потому что именно охват
        объясняет ширину вилки. Показать одну границу без другой значило бы
        выдать за отдачу рекламы либо полноту разметки, либо весь бизнес целиком.
      */}
      <Card
        title="ROMI"
        hint={t(TX.romiHint)}
      >
        {/*
          ROMI ДВА — по праздникам и по онлайн-билетам (решение 02.11.2026), и
          знаменатель каждого — расход кампаний своей темы по названию
          (`server/src/core/adSpendTopic.ts`). Раньше выручку праздников делили
          на весь расход, включая кампании продажи билетов, и нижняя граница по
          Yas + Dalma за октябрь выходила +8,8 % вместо ≈ +127 %.

          У границ вилки сравнения НЕТ, и это осознанно: низ измеряет полноту
          атрибуции, верх — в основном сезонность. Сравнивается охват и расход.
        */}
        {([
          ["banquet", romi.banquet],
          ["tickets", romi.tickets],
        ] as const).map(([topic, r]) => {
          const isB = topic === "banquet";
          const unit = isB ? TX.events : TX.bookings;
          return (
            <div key={topic} style={{ marginBottom: 12 }}>
              <div className="muted" style={{ fontWeight: 600, margin: "4px 0 8px" }}>
                {t(isB ? TX.romiBanquets : TX.romiTickets)}
              </div>
              <div className="grid cols-4">
                <Kpi
                  label={t(TX.lowerBound)}
                  value={r.romi_attributed_pct === null ? "—" : pctValue(r.romi_attributed_pct)}
                  foot={`${t(isB ? TX.footRomiLower : TX.footRomiLowerTickets)} · ${aedShort(r.attributed_revenue_aed_minor)} AED ${t(TX.fromEvents)} ${num(r.attributed_count)} ${plural(r.attributed_count, unit)}`}
                  drill={isB ? {
                    kind: "deals",
                    title: `ROMI — ${t(TX.drillRomiLower)}`,
                    hint: t(TX.drillHintRomiNumerator),
                    slice: { dateBasis: "party", outcome: "held", isPaidSource: true },
                  } : undefined}
                />
                <Kpi
                  label={t(TX.upperBound)}
                  value={r.romi_upper_pct === null ? "—" : pctValue(r.romi_upper_pct)}
                  foot={`${t(isB ? TX.footRomiUpper : TX.footRomiUpperTickets)} · ${aedShort(r.all_revenue_aed_minor)} AED ${t(TX.fromEvents)} ${num(r.all_count)} ${plural(r.all_count, unit)}`}
                  drill={isB ? {
                    kind: "deals",
                    title: `ROMI — ${t(TX.drillRomiUpper)}`,
                    hint: t(TX.drillHintRomiNumerator),
                    slice: { dateBasis: "party", outcome: "held" },
                  } : {
                    kind: "tickets",
                    title: `ROMI — ${t(TX.drillRomiUpperTickets)}`,
                    hint: t(TX.drillHintRomiNumerator),
                    slice: { basis: "paid" },
                  }}
                />
                <Kpi
                  label={t(TX.attributionCoverage)}
                  value={r.attribution_coverage_pct === null ? "—" : pctValue(r.attribution_coverage_pct)}
                  foot={`${num(r.attributed_count)} ${t(UI.of)} ${num(r.all_count)} ${plural(r.all_count, isB ? TX.footCoverageEvents : TX.footCoverageBookings)}`}
                  delta={delta?.(`romi.${topic}.attribution_coverage_pct`)}
                  /* Открывается ТОЛЬКО числитель: знаменатель стоит рядом верхом вилки. */
                  drill={isB ? {
                    kind: "deals",
                    title: `${t(TX.attributionCoverage)} — ${t(TX.drillCoverageNumerator)}`,
                    hint: t(TX.drillHintCoverageNumerator),
                    slice: { dateBasis: "party", outcome: "held", isPaidSource: true },
                  } : undefined}
                />
                {/* Расход — агрегат кабинета по кампании за день, детализации нет. */}
                <Kpi
                  label={t(isB ? TX.spendBanquet : TX.spendTickets)}
                  value={aedShort(r.spend_aed_minor)}
                  unit="AED"
                  foot={t(isB ? TX.footSpendBanquet : TX.footSpendTickets)}
                  delta={delta?.(`romi.${topic}.spend_aed_minor`)}
                />
              </div>
            </div>
          );
        })}
        <div className="note" style={{ marginTop: 4 }}>
          {t(TX.romiOther1)} <b>{aedShort(romi.other_spend_aed_minor)} AED</b> {t(TX.romiOther2)}{" "}
          {aedShort(romi.spend_aed_minor)} AED.
        </div>
        <div className="note" style={{ marginTop: 12 }}>
          {t(TX.romiNote1)} {num(romi.banquet.all_count)} {t(TX.romiNote2)}{" "}
          {num(romi.banquet.attributed_count)} {t(TX.romiNote3)} <code>gclid</code>{" "}
          {t(TX.romiNote4)}
        </div>
      </Card>

      {/*
        Раздельно по платформам — прямая просьба заказчика.
        Конверсии кабинета и лиды CRM стоят в одной таблице, но это разные
        счётчики: кабинет знает свои конверсии, CRM знает лиды. Складывать их
        нельзя, и поэтому колонки подписаны источником, а не одним словом «лиды».
      */}
      <Card
        title={t(TX.byPlatform)}
        hint={t(TX.byPlatformHint)}
      >
        <div className="scroll-x">
          <table className="data">
            <thead>
              <tr>
                <Th sort={sPlatform} col="platform">{t(TX.colPlatform)}</Th>
                <Th sort={sPlatform} col="spend" num>{t(TX.colSpendAed)}</Th>
                <Th sort={sPlatform} col="clicks" num>{t(TX.colClicks)}</Th>
                <Th sort={sPlatform} col="leadConv" num>{t(TX.leadConversions)}</Th>
                <Th sort={sPlatform} col="other" num>{t(TX.colOtherConversions)}</Th>
                <Th sort={sPlatform} col="crmLeads" num>{t(TX.colLeadsInCrm)}</Th>
                <Th sort={sPlatform} col="qualified" num>{t(TX.colQualified)}</Th>
                <Th sort={sPlatform} col="won" num>{t(TX.colCompletedEvents)}</Th>
                <Th sort={sPlatform} col="perLead" num>{t(TX.colCostPerCrmLead)}</Th>
              </tr>
            </thead>
            <tbody>
              {sPlatform.rows.map((pl) => {
                const spendMinor = Number(pl.spend_aed_minor);
                const other =
                  Number(pl.engagement_conversions) + Number(pl.awareness_conversions);
                /*
                 * Срез — `source_group`, а НЕ платформа: плитка считает лидов
                 * именно по нему (`crm` в `api/marketing.ts` сводит
                 * `google_ads` → google, `meta_ads` → meta), и среза «платформа»
                 * у списка лидов нет. Отбор по платформе дал бы 611 строк под
                 * числом 498 — это уже мерили на сервере.
                 */
                const platformName = pl.platform === "google" ? "Google Ads" : "Meta Ads";
                const sourceGroup = pl.platform === "google" ? "google_ads" : "meta_ads";
                return (
                  <tr key={pl.platform}>
                    <td>
                      {pl.platform === "google" ? "Google Ads" : "Meta Ads"}
                      {pl.crm_leads_analytics_tag > 0 && (
                        <div className="drill-hint">
                          {num(pl.crm_leads_analytics_tag)}{" "}
                          {plural(pl.crm_leads_analytics_tag, TX.hintTaggedByAnalytics)}
                        </div>
                      )}
                    </td>
                    <td className="num">{aed(spendMinor)}</td>
                    <td className="num">{num(Number(pl.clicks))}</td>
                    <td className="num">{num(Number(pl.lead_conversions))}</td>
                    <td className="num">{other === 0 ? "—" : num(other)}</td>
                    <td className="num">
                      {leadCell(
                        pl.crm_leads,
                        `${platformName} — ${t(TX.drillLeadsInCrm)}`,
                        `${t(TX.drillHintPlatform)} · ${t(TX.drillHintCreated)}`,
                        { sourceGroup },
                      )}
                    </td>
                    <td className="num">
                      {leadCell(
                        pl.crm_qualified,
                        `${platformName} — ${t(TX.drillQualified)}`,
                        t(TX.drillHintCreated),
                        { sourceGroup, qualified: true },
                      )}
                    </td>
                    {/* «Завершённые праздники» здесь — ЛИДЫ с выигранной
                        сделкой (`p.has_won_deal`), а не сами сделки: когорта
                        плитки — лиды периода. Список сделок по дате закрытия
                        совпал бы с числом случайно (замер: 1 и там и там) и
                        разошёлся бы в первом же периоде, где лид и его праздник
                        попали в разные месяцы. */}
                    <td className="num">
                      {leadCell(
                        pl.crm_won,
                        `${platformName} — ${t(TX.drillLeadsWon)}`,
                        t(TX.drillHintCreated),
                        { sourceGroup, won: true },
                      )}
                    </td>
                    <td className="num">
                      {pl.crm_leads > 0 && spendMinor > 0
                        ? ratioCell(
                            (spendMinor / pl.crm_leads / 100).toFixed(0),
                            `${platformName} — ${t(TX.colCostPerCrmLead)}`,
                            { sourceGroup },
                          )
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="note" style={{ marginTop: 12 }}>
          <b>{t(TX.leadConversions)}</b> {t(TX.platformNote1)}{" "}
          <b>{t(TX.colLeadsInCrm)}</b> {t(TX.platformNote2)} <code>gclid</code>{" "}
          {t(TX.platformNote3)}
        </div>
      </Card>

      {/*
        Источники со стороны CRM. То же определение, что в разрезе Sources на
        Overview: если бы здесь считалось иначе, два экрана начали бы спорить.
      */}
      <Card title={t(TX.bySource)} hint={t(TX.bySourceHint)}>
        <div className="scroll-x">
          <table className="data">
            <thead>
              <tr>
                <Th sort={sSource} col="source">{t(UI.source)}</Th>
                <Th sort={sSource} col="leads" num>{t(UI.conversations)}</Th>
                <Th sort={sSource} col="qualified" num>{t(TX.colQualified)}</Th>
                <Th sort={sSource} col="withDeal" num>{t(TX.colBecameDeal)}</Th>
                <Th sort={sSource} col="won" num>{t(TX.colCompletedEvents)}</Th>
                <Th sort={sSource} col="revenue" num>{t(TX.colRevenueAed)}</Th>
              </tr>
            </thead>
            <tbody>
              {sSource.rows.map((r) => {
                /* Подпись строки и заголовок списка обязаны называться
                   одинаково, иначе человек не узнает, откуда пришёл. */
                const label = dbLabel(CHANNEL, r.source_group, r.source_name);
                return (
                  <tr key={r.source_group}>
                    <td>
                      {label}
                      {r.basis_analytics_tag > 0 && (
                        <div className="drill-hint">
                          {num(r.basis_analytics_tag)} {t(TX.hintTaggedNotCrm)}
                        </div>
                      )}
                    </td>
                    <td className="num">
                      {leadCell(
                        r.leads,
                        `${label} — ${t(TX.drillAllLeads)}`,
                        t(TX.drillHintCreated),
                        { sourceGroup: r.source_group },
                      )}
                    </td>
                    <td className="num">
                      {leadCell(
                        r.qualified,
                        `${label} — ${t(TX.drillQualified)}`,
                        t(TX.drillHintCreated),
                        { sourceGroup: r.source_group, qualified: true },
                      )}
                    </td>
                    <td className="num">
                      {leadCell(
                        r.with_deal,
                        `${label} — ${t(TX.drillLeadsWithDeal)}`,
                        t(TX.drillHintCreated),
                        { sourceGroup: r.source_group, hasDeal: true },
                      )}
                    </td>
                    <td className="num">
                      {leadCell(
                        r.won,
                        `${label} — ${t(TX.drillCompletedEvents)}`,
                        t(TX.drillHintCreated),
                        { sourceGroup: r.source_group, won: true },
                      )}
                    </td>
                    {/* Выручка НЕ раскрывается, и это не пропуск: на плитке
                        деньги, а не число строк. Складывает их серверный
                        `LATERAL` по ВСЕМ выигранным сделкам лида, а список лидов
                        печатает у лида одну сделку — суммы в столбце и на плитке
                        разошлись бы у первого же лида с двумя праздниками.
                        Соседняя колонка «завершённые праздники» — те же самые
                        лиды, и она открывается. */}
                    <td className="num">{aed(r.revenue_aed_minor)}</td>
                  </tr>
                  );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/*
        Онлайн-билеты по источникам — сквозная аналитика сайта билетов. Стоит
        сразу за источниками лидов CRM: это та же постановка вопроса («откуда
        пришли»), но про покупку, а не про обращение, и с другим определением
        источника (`core.v_ticket_booking_source`, миграция 093).
      */}
      <TicketSourcesSection report={report.tickets} from={report.filters.from} delta={delta} />

      {unknown > 0 && (
        <div className="note" style={{ marginBottom: 14 }}>
          {aedShort(unknown)} AED {t(TX.unknownSpendNote)}
        </div>
      )}

      <Card title={t(TX.byPark)} hint={t(TX.byParkHint)}>
        <div className="scroll-x">
          <table className="data">
            <thead>
              <tr>
                <Th sort={sPark} col="park">{t(UI.park)}</Th>
                <Th sort={sPark} col="spend" num>{t(TX.colAdSpendAed)}</Th>
                <Th sort={sPark} col="conversations" num>{t(TX.colConversations)}</Th>
                <Th sort={sPark} col="perConv" num>{t(TX.colCostPerConversation)}</Th>
                <Th sort={sPark} col="leads" num>{t(TX.colLeadsCrm)}</Th>
                <Th sort={sPark} col="perLead" num>{t(TX.colCostPerLead)}</Th>
                <Th sort={sPark} col="revenue" num>{t(TX.colPosRevenueAed)}</Th>
                <Th sort={sPark} col="adsPerRevenue" num>{t(TX.colAdsPerRevenue)}</Th>
              </tr>
            </thead>
            <tbody>
              {sPark.rows.map((p) => {
                const s = Number(p.spend_aed_minor);
                const conv = Number(p.conversations);
                const rev = Number(p.revenue_aed_minor);
                const parkLabel = dbLabel(SENTINEL, p.park_name, p.park_name);
                return (
                  <tr key={p.park_key} style={!p.has_ad_data ? { color: "var(--ink-3)" } : undefined}>
                    <td>{parkLabel}</td>
                    {/* Расход и диалоги — агрегат рекламного кабинета по
                        кампании за день; касса Syrve приходит отчётом с
                        группировкой по дню, отделу и блюду, и идентификатора
                        чека в источнике НЕТ. Ни одна из этих трёх колонок не
                        раскрывается ни лидом, ни чеком — см. отчёт волны. */}
                    <td className="num">
                      {p.has_ad_data
                        ? aed(s)
                        : p.has_ad_account
                          ? t(TX.noSpendInPeriod)
                          : t(TX.noAdAccount)}
                    </td>
                    <td className="num">{p.has_ad_data ? num(conv) : "—"}</td>
                    <td className="num">{conv > 0 ? (s / conv / 100).toFixed(0) : "—"}</td>
                    <td className="num">
                      {leadCell(
                        p.leads,
                        `${parkLabel} — ${t(TX.drillParkLeads)}`,
                        t(TX.drillHintCreated),
                        { parkKey: p.park_key },
                      )}
                    </td>
                    <td className="num">
                      {p.has_ad_data && p.leads > 0
                        ? ratioCell(
                            (s / p.leads / 100).toFixed(0),
                            `${parkLabel} — ${t(TX.colCostPerLead)}`,
                            { parkKey: p.park_key },
                          )
                        : "—"}
                    </td>
                    <td className="num">{p.has_pos_data ? aed(rev) : t(TX.noSyrveAccess)}</td>
                    <td className="num">
                      {p.has_pos_data && rev > 0 && s > 0 ? pctValue((100 * s) / rev, 2) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="note" style={{ marginTop: 12 }}>
          <b>{t(TX.costPerConversationKpi)}</b> {t(TX.parkNoteExact)}{" "}
          <b>{t(TX.costPerLeadInline)}</b> {t(TX.parkNoteUpperBound)}{" "}
          <b>{t(TX.colAdsPerRevenue)}</b> {t(TX.parkNoteRatio)}
          {era === "all" && (
            <>
              {" "}
              {t(TX.parkNoteLegacy)}
            </>
          )}
        </div>
      </Card>

      <div className="grid cols-2" style={{ marginTop: 14, marginBottom: 14 }}>
        {/* График: столбик расхода и линия диалогов — месячные агрегаты
            кабинета, за ними лида нет. Линия лидов CRM раскрывалась бы, но
            только целым месяцем, а месяц в графике — не тот период, что стоит в
            шапке экрана: щелчок по августу открыл бы список за выбранный период
            целиком под числом одного месяца. Не обвязано. */}
        <Card title={t(TX.spendAndLeadsByMonth)} hint={t(TX.twoAxes)}>
          <MarketingTrend monthly={report.monthly} />
        </Card>
      </div>

      {/* КАМПАНИИ БЕЗ ПАРКА — рабочий список для правил именования, и перехода
          у него нет по той же причине, что у воронки по кампаниям:
          `v_ad_unattributed` отдаёт только ИМЯ кампании, а срез сделок отбирает
          по `campaign_external_id`. Расход и диалоги в строках — агрегат
          кабинета. Что тут нужно человеку — это правило разбора имени, и оно
          заводится в базе, а не открывается списком. */}
      {report.unattributed.length > 0 && (
        <Card title={t(TX.campaignsWithNoPark)} hint={t(TX.campaignsWithNoParkHint)}>
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <Th sort={sUnattributed} col="name">{t(UI.campaign)}</Th>
                  <Th sort={sUnattributed} col="objective">{t(TX.colObjective)}</Th>
                  <Th sort={sUnattributed} col="period">{t(TX.colPeriod)}</Th>
                  <Th sort={sUnattributed} col="spend" num>AED</Th>
                  <Th sort={sUnattributed} col="conversations" num>{t(TX.colConversations)}</Th>
                </tr>
              </thead>
              <tbody>
                {sUnattributed.rows.map((u) => (
                  <tr key={u.name}>
                    <td>{u.name}</td>
                    <td>{(u.objective ?? "—").replace("OUTCOME_", "")}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {u.first_day} → {u.last_day}
                    </td>
                    <td className="num">{aed(u.spend_aed_minor)}</td>
                    <td className="num">{num(Number(u.messaging_conversations))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title={t(TX.topCampaigns)} hint={t(TX.topCampaignsHint)}>
        <div className="scroll-x">
          <table className="data">
            <thead>
              <tr>
                <Th sort={sCampaign} col="campaign">{t(UI.campaign)}</Th>
                <Th sort={sCampaign} col="park">{t(UI.park)}</Th>
                <Th sort={sCampaign} col="spend" num>AED</Th>
                <Th sort={sCampaign} col="clicks" num>{t(TX.colClicks)}</Th>
                <Th sort={sCampaign} col="conversations" num>{t(TX.colConversations)}</Th>
                <Th sort={sCampaign} col="perConv" num>{t(TX.colCostPerConversation)}</Th>
                <Th sort={sCampaign} col="leadForms" num>{t(TX.colLeadForms)}</Th>
              </tr>
            </thead>
            <tbody>
              {sCampaign.rows.map((c) => {
                const s = Number(c.spend_aed_minor);
                const conv = Number(c.conversations);
                return (
                  <tr key={c.campaign_external_id}>
                    <td>
                      {/*
                        Единственный переход, который на этом экране вообще
                        возможен от кампании: `DealSlice.campaignExternalId`
                        есть, а `LeadSlice.campaignExternalId` — нет, поэтому
                        открываются СДЕЛКИ, а не лиды. Замер на 01.07-10.09: из
                        сорока показанных кампаний непустой список дают пять по
                        дате создания и три по дате закрытия — отсюда и подпись
                        панели, и примечание под таблицей: пустой список здесь
                        значит «метка не дошла», а не «сделок не было».
                      */}
                      {openDeals === null
                        ? c.campaign_name
                        : linkTo(c.campaign_name, () =>
                            openDeals({
                              kind: "deals",
                              title: `${c.campaign_name} — ${t(TX.drillCampaignDeals)}`,
                              hint: t(TX.drillHintCampaignDeals),
                              slice: {
                                dateBasis: "create",
                                campaignExternalId: c.campaign_external_id,
                              },
                            }),
                          )}
                      <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                        {c.account_name}
                        {c.objective ? ` · ${c.objective.replace("OUTCOME_", "")}` : ""}
                      </div>
                    </td>
                    <td style={c.attribution_kind !== "park" ? { color: "var(--ink-3)" } : undefined}>
                      {c.park_name === null ? "—" : dbLabel(SENTINEL, c.park_name, c.park_name)}
                    </td>
                    <td className="num">{aed(s)}</td>
                    <td className="num">{num(Number(c.clicks))}</td>
                    <td className="num">{num(conv)}</td>
                    <td className="num">{conv > 0 ? (s / conv / 100).toFixed(0) : "—"}</td>
                    <td className="num">
                      {num(Number(c.platform_conversions))}
                      {c.conversion_kind !== "lead" && (
                        <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                          {dbLabel(CONVERSION_KIND, c.conversion_kind, c.conversion_kind)}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="note" style={{ marginTop: 12 }}>
          {t(TX.topCampaignsDrillNote)}
        </div>
      </Card>

      {/*
        КАМПАНИЯ → ЛИД → СДЕЛКА: ни одна колонка не раскрывается, и это упёрлось
        в две стены сразу, а не в лень.

        Первая — идентификатора кампании в этом ответе НЕТ: `v_campaign_funnel`
        отдаёт `campaign_name`, а срез списка отбирает по
        `campaign_external_id`. Отбирать по имени нечем: такого признака у
        списка нет, а два кабинета вполне могут назвать кампании одинаково.

        Вторая — таблица СОЗНАТЕЛЬНО не подчиняется ни периоду, ни эпохе (об
        этом стоит подпись на самой таблице), а список подчиняется обоим. Даже с
        появлением идентификатора список под «Birthday Lead Gen - DFC_Leads»
        (данные за январь-июнь 2025) на периоде 01.07-10.09.2026 дал бы ноль
        строк под числом 67.

        Чтобы это раскрыть, нужны оба среза сразу — точные формулировки в отчёте
        волны. Половина работы (только идентификатор) сделала бы список,
        показывающий не то число, что в строке.
      */}
      {report.campaignFunnel.length > 0 && (
        <Card
          title={t(TX.campaignLeadDeal)}
          hint={t(TX.campaignLeadDealHint)}
        >
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <Th sort={sFunnel} col="campaign">{t(UI.campaign)}</Th>
                  <Th sort={sFunnel} col="park">{t(UI.park)}</Th>
                  <Th sort={sFunnel} col="spend" num>{t(TX.colSpendAed)}</Th>
                  <Th sort={sFunnel} col="measured" num>{t(TX.colMeasured)}</Th>
                  <Th sort={sFunnel} col="inferred" num>{t(TX.colInferred)}</Th>
                  <Th sort={sFunnel} col="leads" num>{t(UI.leads)}</Th>
                  <Th sort={sFunnel} col="withDeal" num>{t(UI.deals)}</Th>
                  <Th sort={sFunnel} col="won" num>{t(TX.colCompleted)}</Th>
                  <Th sort={sFunnel} col="revenue" num>{t(TX.colRevenueAed)}</Th>
                </tr>
              </thead>
              <tbody>
                {sFunnel.rows.map((c) => (
                  <tr key={c.campaign_name}>
                    <td>
                      {c.campaign_name}
                      <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                        {c.first_day} → {c.last_day}
                      </div>
                    </td>
                    <td>{c.park_key ?? "—"}</td>
                    <td className="num">{aed(c.spend_aed_minor)}</td>
                    <td className="num">{num(c.leads_with_utm)}</td>
                    <td className="num">{num(c.leads_with_inferred_tag)}</td>
                    <td className="num">{num(c.leads_total)}</td>
                    <td className="num">{num(c.with_deal)}</td>
                    <td className="num">{num(c.won_deal)}</td>
                    <td className="num">{aed(c.revenue_aed_minor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="note" style={{ marginTop: 12 }}>
            <b>{t(TX.funnelIgnoresFilter)}</b> {t(TX.funnelNote1)}{" "}
            {num(report.campaignFunnel.reduce((s, c) => s + c.leads_total, 0))}{" "}
            {t(TX.funnelNote2)}
            <br />
            <b>{t(TX.colMeasured)}</b> {t(TX.funnelNote3)}{" "}
            <b>{t(TX.colInferred)}</b> {t(TX.funnelNote4)}{" "}
            <b>{t(UI.leads)}</b> {t(TX.funnelNote5)} <b>{t(TX.notWord)}</b>{" "}
            {t(TX.funnelNote6)}
            {report.utmLevels.length > 0 && (
              <>
                <br />
                {t(TX.utmLevelsNote)}{" "}
                {report.utmLevels
                  .map(
                    (u) =>
                      `${u.utm_field} ${t(TX.utmHolds)} ${u.utm_level} id (${num(u.leads)} ${plural(u.leads, TX.leadsCounted)})`,
                  )
                  .join(", ")}
                {t(TX.utmLevelsTail)}
              </>
            )}
          </div>
        </Card>
      )}

      {/* ПОКРЫТИЕ КАБИНЕТОВ: строка — рекламный кабинет, и чисел в ней нет
          вовсе, только валюта, границы данных и состояние. Среза «кабинет» нет
          ни у лидов, ни у сделок, да и связи «лид → кабинет» в данных не
          существует: до лида не доходит даже кампания. Переход отсюда вёл бы
          в никуда. */}
      <Card title={t(TX.adAccountCoverage)} hint={t(TX.adAccountCoverageHint)}>
        <div className="scroll-x">
          <table className="data">
            <thead>
              <tr>
                <Th sort={sCoverage} col="account">{t(TX.colAccount)}</Th>
                <Th sort={sCoverage} col="platform">{t(TX.colPlatform)}</Th>
                <Th sort={sCoverage} col="currency">{t(TX.colCurrency)}</Th>
                <Th sort={sCoverage} col="brand">{t(UI.brand)}</Th>
                <Th sort={sCoverage} col="dataFrom">{t(TX.colDataFrom)}</Th>
                <Th sort={sCoverage} col="dataTo">{t(TX.colDataTo)}</Th>
                <Th sort={sCoverage} col="state">{t(TX.colState)}</Th>
              </tr>
            </thead>
            <tbody>
              {sCoverage.rows.map((c) => (
                <tr
                  key={`${c.platform}-${c.external_id}`}
                  style={c.dormant ? { color: "var(--ink-3)" } : undefined}
                >
                  <td>{c.name}</td>
                  <td>{c.platform}</td>
                  <td>{c.currency}</td>
                  <td>{c.brand_key ?? "—"}</td>
                  <td>{c.first_day ?? "—"}</td>
                  <td>{c.last_day ?? "—"}</td>
                  <td>
                    {c.dormant
                      ? t(TX.stateDormant)
                      : c.enabled
                        ? t(TX.stateActive)
                        : t(TX.stateDisabled)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="note" style={{ marginTop: 12 }}>
          {noAds.length > 0 && (
            <>
              {t(TX.noAdAccountNote1)} {noAds.map((p) => p.park_name).join(", ")}{" "}
              {t(TX.noAdAccountNote2)}{" "}
            </>
          )}
          {dormant.length > 0 && (
            <>
              {dormant.map((c) => c.name).join(", ")} {plural(dormant.length, TX.dormantKept)}
            </>
          )}
        </div>
      </Card>
    </>
  );
}

/** Траты и лиды на одной сетке: две оси, потому что величины разного порядка. */
function MarketingTrend({ monthly }: { monthly: MarketingReport["monthly"] }): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const tick = useThemeTick();

  useEffect(() => {
    if (!ref.current) return;
    // Цвета читаются здесь, в момент отрисовки: тему могли переключить.
    const t = chartTheme();
    // Подпись оси из общего набора, поверх неё — свой формат чисел.
    const axisLabel = t.axis.axisLabel as Record<string, unknown>;
    const chart = echarts.init(ref.current, undefined, { renderer: "canvas" });
    chart.setOption({
      grid: { left: 52, right: 52, top: 28, bottom: 24 },
      tooltip: { trigger: "axis", ...t.tooltip },
      legend: {
        top: 0,
        textStyle: { color: t.ink2, fontFamily: t.font },
        itemHeight: 8,
        itemWidth: 12,
      },
      xAxis: {
        type: "category",
        data: monthly.map((m) => m.month.slice(0, 7)),
        ...t.axis,
      },
      yAxis: [
        {
          type: "value",
          name: "AED",
          nameTextStyle: { color: t.ink3, fontSize: 10, fontFamily: t.font },
          ...t.axis,
          splitLine: t.splitLine,
          axisLabel: { ...axisLabel, formatter: (v: number) => compact(v) },
        },
        {
          type: "value",
          name: chartLabel(TX.axisCount),
          nameTextStyle: { color: t.ink3, fontSize: 10, fontFamily: t.font },
          ...t.axis,
          splitLine: { show: false },
          axisLabel: { ...axisLabel, formatter: (v: number) => compact(v) },
        },
      ],
      series: [
        {
          name: chartLabel(TX.colAdSpendAed),
          type: "bar",
          itemStyle: { color: t.series[0], borderRadius: [3, 3, 0, 0] },
          data: monthly.map((m) => Number(m.spend_aed_minor) / 100),
        },
        {
          name: chartLabel(TX.colLeadsCrm),
          type: "line",
          yAxisIndex: 1,
          smooth: true,
          symbolSize: 6,
          lineStyle: { width: 2 },
          itemStyle: { color: t.series[1] },
          data: monthly.map((m) => m.leads),
        },
        {
          name: chartLabel(TX.whatsappConversations),
          type: "line",
          yAxisIndex: 1,
          smooth: true,
          symbolSize: 5,
          lineStyle: { width: 2, type: "dashed" },
          itemStyle: { color: t.series[2] },
          data: monthly.map((m) => Number(m.conversations)),
        },
      ],
    });
    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(ref.current);
    return () => {
      ro.disconnect();
      chart.dispose();
    };
  }, [monthly, tick]);

  return <div ref={ref} style={{ height: 260 }} />;
}
